import { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:5050";
const ROOM_COL_W = 190;
const DAY_COL_W = 40;
const ROW_H = 62;
const BAR_H = 28;
const BAR_TOP = (ROW_H - BAR_H) / 2;

const authHeaders = {
  Authorization: `Bearer ${localStorage.getItem("ai2_token")}`,
  "x-hotel": localStorage.getItem("ai2_hotel") || "",
};

interface Booking {
  bookingid: string;
  roomid: string;
  guestname: string;
  leaderName?: string;
  leaderPhone?: string | null;
  checkin: string;
  checkout: string;
  nights: number;
  total: number;
  status: string;
}

interface Room {
  roomid: string;
  roomnumber: string | number;
  roomtype: string;
  status: string;
}

interface TooltipInfo {
  booking: Booking;
  roomid: string;
  x: number;
  y: number;
}

interface PopupInfo {
  booking: Booking;
  roomid: string;
  x: number;
  y: number;
}

const STATUS_COLOR: Record<string, string> = {
  confirmed: "#1a3a5c",
  "checked-in": "#10b981",
  "checked-out": "#64748b",
  pending: "#f59e0b",
  "on-hold": "#f97316",
  cancelled: "#ef4444",
};

function localISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function addDays(d: Date, n: number): Date {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + n);
  return copy;
}

function parseLocalDate(iso: string): Date {
  const [y, m, day] = iso.split("-").map(Number);
  return new Date(y, m - 1, day);
}

function formatDayHeader(d: Date): string {
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return `${days[d.getDay()]} ${d.getDate()}`;
}

function formatDateRange(start: Date, days: number): string {
  const end = addDays(start, days - 1);
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" };
  return `${start.toLocaleDateString("en-US", opts)} — ${end.toLocaleDateString("en-US", opts)}`;
}

function formatDateShort(iso: string): string {
  const d = parseLocalDate(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatCurrency(n: number): string {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

export default function BookingCalendarPage() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [startDate, setStartDate] = useState<Date>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  const [zoom, setZoom] = useState<14 | 28>(28);
  const [roomTypeFilter, setRoomTypeFilter] = useState("all");
  const [guestSearch, setGuestSearch] = useState("");
  const [tooltip, setTooltip] = useState<TooltipInfo | null>(null);
  const [popup, setPopup] = useState<PopupInfo | null>(null);
  const [newBookingModal, setNewBookingModal] = useState<{
    roomid: string; roomNumber: string | number; dateISO: string;
  } | null>(null);
  const [newBookingForm, setNewBookingForm] = useState({
    guestname: "", phonenumber: "", checkout: "", total: "", guest_count: "1",
  });
  const [savingBooking, setSavingBooking] = useState(false);
  const [bookingError, setBookingError] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const todayISO = localISO(new Date());

  useEffect(() => {
    setLoading(true);
    setError("");
    Promise.all([
      axios.get(`${API_BASE}/api/dashboard/bookings`, { headers: authHeaders }),
      axios.get(`${API_BASE}/api/rooms`, { headers: authHeaders }),
    ])
      .then(([bRes, rRes]) => {
        // Use calendarEntries — one entry per booking_rooms row joined with bookings
        const rawEntries: any[] = bRes.data.calendarEntries || [];
        const mappedBookings: Booking[] = rawEntries.map((b) => ({
          bookingid:   b.bookingid  || "",
          roomid:      b.roomid     || "",
          guestname:   b.guestname  || "—",
          leaderName:  b.leaderName || b.guestname || "—",
          leaderPhone: b.leaderPhone || null,
          checkin:     b.checkin    || "",
          checkout:    b.checkout   || "",
          nights:      b.nights     || 0,
          total:       b.total      || 0,
          status:      b.status     || "confirmed",
        }));
        const rawRooms = Array.isArray(rRes.data)
          ? rRes.data
          : rRes.data.rooms || rRes.data.data || [];
        setBookings(mappedBookings);
        setRooms(rawRooms);
      })
      .catch(() => {
        axios
          .get(`${API_BASE}/api/dashboard/room-status`, { headers: authHeaders })
          .then((res) => {
            const raw = Array.isArray(res.data) ? res.data : res.data.rooms || [];
            setRooms(raw);
          })
          .catch(() => setError("Failed to load calendar data"));
      })
      .finally(() => setLoading(false));
  }, []);

  const roomTypes = useMemo(() => {
    const types = new Set(rooms.map((r) => r.roomtype));
    return ["all", ...Array.from(types).sort()];
  }, [rooms]);

  const filteredRooms = useMemo(() => {
    if (roomTypeFilter === "all") return rooms;
    return rooms.filter((r) => r.roomtype === roomTypeFilter);
  }, [rooms, roomTypeFilter]);

  const visibleDates = useMemo(() => {
    return Array.from({ length: zoom }, (_, i) => {
      const d = addDays(startDate, i);
      return { date: d, iso: localISO(d) };
    });
  }, [startDate, zoom]);

  const endISO = visibleDates.length > 0 ? visibleDates[visibleDates.length - 1].iso : todayISO;

  const activeBookings = useMemo(() => {
    return bookings.filter(
      (b) => b.status !== "cancelled" && b.status !== "deleted" && b.status !== "checked-out"
    );
  }, [bookings]);

  const bookingsByRoom = useMemo(() => {
    const map: Record<string, Booking[]> = {};
    for (const b of activeBookings) {
      if (!b.roomid) continue;
      if (!map[b.roomid]) map[b.roomid] = [];
      map[b.roomid].push(b);
    }
    return map;
  }, [activeBookings]);

  const conflicts = useMemo(() => {
    const found: { roomid: string; b1: Booking; b2: Booking }[] = [];
    for (const [roomid, bks] of Object.entries(bookingsByRoom)) {
      for (let i = 0; i < bks.length; i++) {
        for (let j = i + 1; j < bks.length; j++) {
          const a = bks[i];
          const b = bks[j];
          const aIn = parseLocalDate(a.checkin).getTime();
          const aOut = parseLocalDate(a.checkout).getTime();
          const bIn = parseLocalDate(b.checkin).getTime();
          const bOut = parseLocalDate(b.checkout).getTime();
          if (aIn < bOut && aOut > bIn) {
            found.push({ roomid, b1: a, b2: b });
          }
        }
      }
    }
    return found;
  }, [bookingsByRoom]);

  const conflictBookingIds = useMemo(() => {
    const ids = new Set<string>();
    for (const c of conflicts) {
      ids.add(c.b1.bookingid);
      ids.add(c.b2.bookingid);
    }
    return ids;
  }, [conflicts]);

  const searchLower = guestSearch.trim().toLowerCase();

  function getBookingBar(
    b: Booking,
    visibleDates: { date: Date; iso: string }[]
  ): { left: number; width: number } | null {
    const startISO = visibleDates[0].iso;
    const endISO = visibleDates[visibleDates.length - 1].iso;
    const bIn = b.checkin;
    const bOut = b.checkout;
    if (bOut <= startISO || bIn > endISO) return null;
    const clampedIn = bIn < startISO ? startISO : bIn;
    const clampedOut = bOut > endISO ? addDays(parseLocalDate(endISO), 1) : parseLocalDate(bOut);
    const inIdx = visibleDates.findIndex((d) => d.iso === clampedIn);
    const startIdx = inIdx >= 0 ? inIdx : 0;
    const inDate = parseLocalDate(clampedIn);
    const outDate = typeof clampedOut === "string" ? parseLocalDate(clampedOut) : clampedOut;
    const days = Math.round((outDate.getTime() - inDate.getTime()) / 86400000);
    const left = startIdx * DAY_COL_W + 2;
    const width = days * DAY_COL_W - 4;
    if (width <= 0) return null;
    return { left, width };
  }

  function handlePrev() {
    setStartDate((d) => addDays(d, -7));
    setPopup(null);
  }

  function handleNext() {
    setStartDate((d) => addDays(d, 7));
    setPopup(null);
  }

  function handleToday() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setStartDate(today);
    setPopup(null);
  }

  function handleBarMouseEnter(e: React.MouseEvent, b: Booking, roomid: string) {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect();
    const rightEdge = rect.right - (containerRect?.left ?? 0);
    const x = Math.min(rightEdge, gridWidth - 224);
    const y = rect.top - (containerRect?.top ?? 0) + BAR_H + 4;
    setTooltip({ booking: b, roomid, x, y });
  }

  function handleBarMouseLeave() {
    setTooltip(null);
  }

  function handleBarClick(e: React.MouseEvent, b: Booking, roomid: string) {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect();
    const rightEdge = rect.right - (containerRect?.left ?? 0);
    const x = Math.min(rightEdge, gridWidth - 244);
    const y = rect.top - (containerRect?.top ?? 0) + BAR_H + 4;
    setTooltip(null);
    setPopup((prev) =>
      prev && prev.booking.bookingid === b.bookingid && prev.roomid === roomid
        ? null
        : { booking: b, roomid, x, y }
    );
  }

  function handleCellClick(roomid: string, dateISO: string) {
    if (popup) { setPopup(null); return; }
    const room = rooms.find(r => r.roomid === roomid);
    setNewBookingForm({ guestname: "", phonenumber: "", checkout: "", total: "", guest_count: "1" });
    setBookingError("");
    setNewBookingModal({ roomid, roomNumber: room?.roomnumber || roomid, dateISO });
  }

  async function submitNewBooking() {
    if (!newBookingModal) return;
    const { guestname, phonenumber, checkout, total } = newBookingForm;
    if (!guestname.trim()) { setBookingError("Guest name is required."); return; }
    if (!checkout) { setBookingError("Check-out date is required."); return; }
    if (checkout <= newBookingModal.dateISO) { setBookingError("Check-out must be after check-in."); return; }

    const checkin = newBookingModal.dateISO;
    const cin = parseLocalDate(checkin);
    const cout = parseLocalDate(checkout);
    const nights = Math.round((cout.getTime() - cin.getTime()) / 86400000);
    const bookingid = `B${String(newBookingModal.roomNumber).padStart(3, "0")}-${checkin.replace(/-/g, "")}-${Math.floor(Math.random() * 900 + 100)}`;

    setSavingBooking(true);
    setBookingError("");
    try {
      const { data } = await axios.post(`${API_BASE}/api/bookings`, {
        bookingid,
        roomid: newBookingModal.roomid,
        guestname: guestname.trim(),
        phonenumber: phonenumber.trim() || null,
        checkin,
        checkout,
        nights,
        total: parseFloat(total) || 0,
        status: "confirmed",
        guest_count: parseInt(newBookingForm.guest_count) || 1,
        createdat: new Date().toISOString(),
      }, { headers: authHeaders });

      const newBooking: Booking = {
        bookingid:  data.bookingid || bookingid,
        roomid:     data.roomid || newBookingModal.roomid,
        guestname:  data.guestname || guestname.trim(),
        leaderName: data.guestname || guestname.trim(),
        checkin:    data.checkin || checkin,
        checkout:   data.checkout || checkout,
        nights:     data.nights || nights,
        total:      Number(data.total) || parseFloat(total) || 0,
        status:     data.status || "confirmed",
      };
      setBookings(prev => [...prev, newBooking]);
      setNewBookingModal(null);
    } catch (err: any) {
      setBookingError(err?.response?.data?.error || "Failed to create booking.");
    } finally {
      setSavingBooking(false);
    }
  }

  const totalCols = zoom;
  const gridWidth = ROOM_COL_W + totalCols * DAY_COL_W;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
const styles: Record<string, any> = {
    page: {
      minHeight: "100vh",
      background: "transparent",
      color: "#F2EBD9",
      padding: "24px",
      boxSizing: "border-box",
    },
    header: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      marginBottom: 20,
      flexWrap: "wrap",
    },
    title: {
      fontSize: 22,
      fontWeight: 700,
      color: "#FDFAF4",
      margin: 0,
      marginRight: 8,
      fontFamily: "'Cormorant Garamond', serif",
    },
    conflictBadge: {
      background: "rgba(239,68,68,0.1)",
      border: "1px solid rgba(239,68,68,0.3)",
      color: "#f87171",
      borderRadius: 8,
      padding: "3px 10px",
      fontSize: 12,
      fontWeight: 600,
    },
    controls: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexWrap: "wrap",
      marginBottom: 16,
    },
    btn: {
      background: "rgba(255,255,255,0.05)",
      border: "1px solid rgba(201,168,76,0.15)",
      color: "rgba(242,235,217,0.6)",
      borderRadius: 8,
      padding: "6px 14px",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.15s",
    },
    btnActive: {
      background: "rgba(201,168,76,0.15)",
      border: "1px solid rgba(201,168,76,0.35)",
      color: "#C9A84C",
    },
    btnToday: {
      background: "rgba(16,185,129,0.08)",
      border: "1px solid rgba(16,185,129,0.25)",
      color: "#34d399",
      borderRadius: 8,
      padding: "6px 14px",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
    },
    rangeLabel: {
      color: "rgba(242,235,217,0.4)",
      fontSize: 13,
      fontWeight: 500,
      padding: "0 4px",
    },
    select: {
      background: "rgba(255,255,255,0.04)",
      border: "1px solid rgba(201,168,76,0.15)",
      borderRadius: 8,
      color: "#F2EBD9",
      fontSize: 13,
      padding: "6px 10px",
      outline: "none",
      cursor: "pointer",
    },
    searchInput: {
      background: "rgba(255,255,255,0.04)",
      border: "1px solid rgba(201,168,76,0.15)",
      borderRadius: 8,
      color: "#F2EBD9",
      fontSize: 13,
      padding: "6px 12px",
      outline: "none",
      width: 180,
    },
    calendarWrapper: {
      background: "#0E1E38",
      border: "1px solid rgba(201,168,76,0.12)",
      borderRadius: 12,
      overflow: "hidden",
      boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
    },
    scrollOuter: {
      overflowX: "auto",
      position: "relative",
    },
    grid: {
      minWidth: gridWidth,
      position: "relative",
    },
    headerRow: {
      display: "flex",
      borderBottom: "1px solid rgba(201,168,76,0.1)",
      background: "#0A1628",
      position: "sticky",
      top: 0,
      zIndex: 10,
    },
    roomHeaderCell: {
      width: ROOM_COL_W,
      minWidth: ROOM_COL_W,
      flexShrink: 0,
      padding: "10px 12px",
      fontSize: 10,
      fontWeight: 600,
      color: "rgba(201,168,76,0.5)",
      letterSpacing: "0.18em",
      textTransform: "uppercase",
      borderRight: "1px solid rgba(201,168,76,0.08)",
      position: "sticky",
      left: 0,
      background: "#0A1628",
      zIndex: 11,
    },
    dateCellHeader: (isToday: boolean, isWeekend: boolean): React.CSSProperties => ({
      width: DAY_COL_W,
      minWidth: DAY_COL_W,
      flexShrink: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "5px 2px",
      gap: 1,
      color: isToday ? "#C9A84C" : isWeekend ? "rgba(242,235,217,0.3)" : "rgba(242,235,217,0.45)",
      background: isToday ? "rgba(201,168,76,0.12)" : isWeekend ? "rgba(255,255,255,0.02)" : "transparent",
      borderRight: "1px solid rgba(201,168,76,0.06)",
      borderBottom: isToday ? "2px solid #C9A84C" : "none",
      userSelect: "none",
    }),
    roomRow: (even: boolean): React.CSSProperties => ({
      display: "flex",
      height: ROW_H,
      borderBottom: "1px solid rgba(201,168,76,0.06)",
      background: even ? "rgba(255,255,255,0.02)" : "transparent",
      position: "relative",
    }),
    roomLabelCell: {
      width: ROOM_COL_W,
      minWidth: ROOM_COL_W,
      flexShrink: 0,
      padding: "0 12px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      gap: 2,
      borderRight: "1px solid rgba(201,168,76,0.08)",
      position: "sticky",
      left: 0,
      zIndex: 5,
      background: "inherit",
      overflow: "hidden",
    },
    datesArea: {
      flex: 1,
      position: "relative",
      display: "flex",
    },
    dayCell: (isToday: boolean, isWeekend: boolean): React.CSSProperties => ({
      width: DAY_COL_W,
      minWidth: DAY_COL_W,
      flexShrink: 0,
      height: "100%",
      borderRight: "1px solid rgba(201,168,76,0.05)",
      background: isToday
        ? "rgba(201,168,76,0.07)"
        : isWeekend
        ? "rgba(255,255,255,0.015)"
        : "transparent",
      cursor: "pointer",
      transition: "background 0.12s",
    }),
    barsOverlay: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      pointerEvents: "none",
    },
    legend: {
      display: "flex",
      gap: 20,
      padding: "12px 16px",
      borderTop: "1px solid rgba(201,168,76,0.1)",
      flexWrap: "wrap",
      background: "#0A1628",
    },
    legendItem: {
      display: "flex",
      alignItems: "center",
      gap: 7,
      fontSize: 12,
      color: "rgba(242,235,217,0.4)",
    },
    legendDot: (color: string): React.CSSProperties => ({
      width: 10,
      height: 10,
      borderRadius: 3,
      background: color,
      flexShrink: 0,
    }),
    tooltip: (x: number, y: number): React.CSSProperties => ({
      position: "absolute",
      left: Math.min(x, gridWidth - 220),
      top: y,
      zIndex: 100,
      background: "#0E1E38",
      border: "1px solid rgba(201,168,76,0.2)",
      borderRadius: 10,
      padding: "10px 14px",
      minWidth: 200,
      maxWidth: 260,
      pointerEvents: "none",
      boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
      color: "#F2EBD9",
    }),
    popup: (x: number, y: number): React.CSSProperties => ({
      position: "absolute",
      left: Math.min(x, gridWidth - 240),
      top: y,
      zIndex: 200,
      background: "#0E1E38",
      border: "1px solid rgba(201,168,76,0.2)",
      borderRadius: 12,
      padding: "14px 16px",
      minWidth: 230,
      maxWidth: 270,
      boxShadow: "0 12px 40px rgba(0,0,0,0.55)",
      color: "#F2EBD9",
    }),
    popupClose: {
      position: "absolute",
      top: 8,
      right: 10,
      background: "none",
      border: "none",
      color: "rgba(242,235,217,0.35)",
      fontSize: 16,
      cursor: "pointer",
      lineHeight: 1,
    },
    loadingText: {
      textAlign: "center",
      padding: "48px 0",
      color: "rgba(242,235,217,0.4)",
      fontSize: 15,
    },
    errorText: {
      textAlign: "center",
      padding: "48px 0",
      color: "#f87171",
      fontSize: 15,
    },
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div style={styles.page}>
          <div style={styles.loadingText}>Loading booking calendar…</div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div style={styles.page}>
          <div style={styles.errorText}>{error}</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div style={styles.page} ref={containerRef} onClick={() => setPopup(null)}>
        {/* Page Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>Booking Calendar</h1>
          {conflicts.length > 0 && (
            <span style={styles.conflictBadge}><FontAwesomeIcon icon={faTriangleExclamation} /> {conflicts.length} conflict{conflicts.length > 1 ? "s" : ""} detected</span>
          )}
        </div>

        {/* Controls */}
        <div style={styles.controls}>
          <button style={styles.btn} onClick={handlePrev}>&#8249; Prev</button>
          <button style={styles.btnToday} onClick={handleToday}>Today</button>
          <button style={styles.btn} onClick={handleNext}>Next &#8250;</button>
          <span style={styles.rangeLabel}>{formatDateRange(startDate, zoom)}</span>

          <div style={{ marginLeft: "auto", display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            {/* Zoom toggle */}
            <div style={{ display: "flex", gap: 4 }}>
              <button
                style={{ ...styles.btn, ...(zoom === 14 ? styles.btnActive : {}) }}
                onClick={() => setZoom(14)}
              >
                14 days
              </button>
              <button
                style={{ ...styles.btn, ...(zoom === 28 ? styles.btnActive : {}) }}
                onClick={() => setZoom(28)}
              >
                28 days
              </button>
            </div>

            {/* Room type filter */}
            <select
              style={styles.select}
              value={roomTypeFilter}
              onChange={(e) => setRoomTypeFilter(e.target.value)}
            >
              {roomTypes.map((t) => (
                <option key={t} value={t}>
                  {t === "all" ? "All room types" : t}
                </option>
              ))}
            </select>

            {/* Guest search */}
            <input
              style={styles.searchInput}
              type="text"
              placeholder="Search guest name…"
              value={guestSearch}
              onChange={(e) => setGuestSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Calendar */}
        <div style={styles.calendarWrapper}>
          <div style={styles.scrollOuter} ref={scrollRef}>
            <div style={styles.grid}>
              {/* Header row */}
              <div style={styles.headerRow}>
                <div style={styles.roomHeaderCell}>Room</div>
                {visibleDates.map(({ date, iso }) => {
                  const isToday = iso === todayISO;
                  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                  const days = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
                  return (
                    <div key={iso} style={styles.dateCellHeader(isToday, isWeekend)}>
                      <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                        {days[date.getDay()]}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: isToday ? 800 : 600, lineHeight: 1 }}>
                        {date.getDate()}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Room rows */}
              {filteredRooms.length === 0 && (
                <div style={{ ...styles.loadingText, paddingLeft: ROOM_COL_W + 16 }}>
                  No rooms found.
                </div>
              )}
              {filteredRooms.map((room, rowIdx) => {
                const roomBookings = (bookingsByRoom[room.roomid] || []).filter((b) => {
                  const bOut = b.checkout;
                  const bIn = b.checkin;
                  const rangeStart = visibleDates[0].iso;
                  const rangeEnd = endISO;
                  return bOut > rangeStart && bIn <= rangeEnd;
                });

                return (
                  <div key={room.roomid} style={styles.roomRow(rowIdx % 2 === 0)}>
                    {/* Sticky room label */}
                    <div
                      style={{
                        ...styles.roomLabelCell,
                        background: rowIdx % 2 === 0 ? "#0A1628" : "#0E1E38",
                      }}
                    >
                      {room.roomtype && <span className="cal-room-line-type">{room.roomtype}</span>}
                      <span className="cal-room-line-num">Room {room.roomnumber}</span>
                      <span className="cal-room-line-id">{room.roomid}</span>
                    </div>

                    {/* Date cells (clickable empty cells) */}
                    <div style={styles.datesArea}>
                      {visibleDates.map(({ date, iso }) => {
                        const isToday = iso === todayISO;
                        const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                        return (
                          <div
                            key={iso}
                            style={styles.dayCell(isToday, isWeekend)}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCellClick(room.roomid, iso);
                            }}
                          />
                        );
                      })}

                      {/* Booking bars overlay */}
                      <div style={styles.barsOverlay}>
                        {roomBookings.map((b) => {
                          const bar = getBookingBar(b, visibleDates);
                          if (!bar) return null;
                          // Use room-specific guest name when available
                          const displayName = b.leaderName || b.guestname;
                          const isConflict = conflictBookingIds.has(b.bookingid);
                          const isHighlighted =
                            searchLower.length > 0 && (
                              b.guestname.toLowerCase().includes(searchLower) ||
                              displayName.toLowerCase().includes(searchLower)
                            );
                          const isDimmed =
                            searchLower.length > 0 && !isHighlighted;
                          const baseColor = isConflict
                            ? "#ef4444"
                            : STATUS_COLOR[b.status] || "#1a3a5c";
                          const barStyle: React.CSSProperties = {
                            position: "absolute",
                            left: bar.left,
                            top: BAR_TOP,
                            width: bar.width,
                            height: BAR_H,
                            borderRadius: 8,
                            background: baseColor,
                            opacity: isDimmed ? 0.25 : 1,
                            outline: isHighlighted ? "2px solid #fff" : isConflict ? "2px solid #fca5a5" : "none",
                            outlineOffset: 1,
                            display: "flex",
                            alignItems: "center",
                            paddingLeft: 8,
                            paddingRight: 6,
                            boxSizing: "border-box",
                            overflow: "hidden",
                            pointerEvents: "all",
                            cursor: "pointer",
                            userSelect: "none",
                            boxShadow: isHighlighted
                              ? `0 0 0 3px rgba(255,255,255,0.4), 0 2px 8px rgba(0,0,0,0.4)`
                              : "0 2px 8px rgba(0,0,0,0.3)",
                            transition: "opacity 0.15s, box-shadow 0.15s",
                            zIndex: 3,
                          };

                          return (
                            <div
                              key={b.bookingid + "-" + room.roomid}
                              style={barStyle}
                              onMouseEnter={(e) => handleBarMouseEnter(e, b, room.roomid)}
                              onMouseLeave={handleBarMouseLeave}
                              onClick={(e) => handleBarClick(e, b, room.roomid)}
                            >
                              {bar.width > 60 && (
                                <span
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: "rgba(255,255,255,0.92)",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    maxWidth: bar.width - 16,
                                    display: "block",
                                  }}
                                >
                                  {displayName}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div style={styles.legend}>
            {[
              { status: "confirmed", label: "Confirmed" },
              { status: "checked-in", label: "Checked In" },
              { status: "on-hold", label: "On Hold" },
              { status: "checked-out", label: "Checked Out" },
              { status: "pending", label: "Pending" },
            ].map(({ status, label }) => (
              <div key={status} style={styles.legendItem}>
                <div style={styles.legendDot(STATUS_COLOR[status])} />
                <span>{label}</span>
              </div>
            ))}
            {conflicts.length > 0 && (
              <div style={styles.legendItem}>
                <div style={styles.legendDot("#ef4444")} />
                <span style={{ color: "#ef4444" }}>Conflict</span>
              </div>
            )}
          </div>
        </div>

        {/* Hover Tooltip */}
        {tooltip && !popup && (() => {
          const leaderName = tooltip.booking.leaderName || tooltip.booking.guestname;
          const mainGuest  = tooltip.booking.guestname;
          const displayPhone = tooltip.booking.leaderPhone || null;
          const showMainGuest = mainGuest && mainGuest !== "—" && mainGuest !== leaderName;
          return (
            <div style={styles.tooltip(tooltip.x, tooltip.y)}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#F2EBD9", marginBottom: 1 }}>
                {leaderName}
              </div>
              {displayPhone && (
                <div style={{ fontSize: 11, color: "rgba(242,235,217,0.4)", marginBottom: 2 }}>{displayPhone}</div>
              )}
              {showMainGuest && (
                <div style={{ fontSize: 11, color: "rgba(242,235,217,0.45)", marginBottom: 6 }}>Booked by: {mainGuest}</div>
              )}
              {!displayPhone && !showMainGuest && <div style={{ marginBottom: 6 }} />}
              <TooltipRow label="Check-in" value={formatDateShort(tooltip.booking.checkin)} />
              <TooltipRow label="Check-out" value={formatDateShort(tooltip.booking.checkout)} />
              <TooltipRow label="Nights" value={String(tooltip.booking.nights)} />
              <TooltipRow
                label="Status"
                value={tooltip.booking.status}
                color={STATUS_COLOR[tooltip.booking.status]}
              />
              <TooltipRow label="Total" value={formatCurrency(tooltip.booking.total)} color="#C9A84C" />
            </div>
          );
        })()}

        {/* New Booking Modal */}
        {newBookingModal && (
          <div
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
              zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
            }}
            onClick={() => setNewBookingModal(null)}
          >
            <div
              style={{
                background: "#0E1E38", border: "1px solid rgba(201,168,76,0.2)",
                borderRadius: 16, padding: 28, width: "100%", maxWidth: 460,
                boxShadow: "0 20px 60px rgba(0,0,0,0.7)",
              }}
              onClick={e => e.stopPropagation()}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: "#F2EBD9" }}>New Booking</div>
                  <div style={{ fontSize: 12, color: "rgba(242,235,217,0.45)", marginTop: 2 }}>
                    Room {newBookingModal.roomNumber} &nbsp;·&nbsp; Check-in: {newBookingModal.dateISO}
                  </div>
                </div>
                <button
                  style={{ background: "none", border: "none", color: "rgba(242,235,217,0.4)", cursor: "pointer", fontSize: 18, padding: 4 }}
                  onClick={() => setNewBookingModal(null)}
                >×</button>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <ModalField label="Guest Name *">
                  <input
                    autoFocus
                    value={newBookingForm.guestname}
                    onChange={e => setNewBookingForm(f => ({ ...f, guestname: e.target.value }))}
                    placeholder="Full name"
                    style={modalInputStyle}
                  />
                </ModalField>
                <ModalField label="Phone Number">
                  <input
                    value={newBookingForm.phonenumber}
                    onChange={e => setNewBookingForm(f => ({ ...f, phonenumber: e.target.value }))}
                    placeholder="+1 234 567 8901"
                    style={modalInputStyle}
                  />
                </ModalField>
                <div style={{ display: "flex", gap: 12 }}>
                  <ModalField label="Check-out Date *">
                    <input
                      type="date"
                      min={newBookingModal.dateISO}
                      value={newBookingForm.checkout}
                      onChange={e => setNewBookingForm(f => ({ ...f, checkout: e.target.value }))}
                      style={modalInputStyle}
                    />
                  </ModalField>
                  <ModalField label="Guests">
                    <select
                      value={newBookingForm.guest_count}
                      onChange={e => setNewBookingForm(f => ({ ...f, guest_count: e.target.value }))}
                      style={modalInputStyle}
                    >
                      {["1","2","3","4","5","6"].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </ModalField>
                </div>
                <ModalField label="Total Amount">
                  <input
                    type="number"
                    value={newBookingForm.total}
                    onChange={e => setNewBookingForm(f => ({ ...f, total: e.target.value }))}
                    placeholder="0"
                    style={modalInputStyle}
                  />
                </ModalField>

                {bookingError && (
                  <div style={{ color: "#ef4444", fontSize: 12, fontWeight: 600, marginTop: -4 }}>
                    {bookingError}
                  </div>
                )}

                <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                  <button
                    style={{
                      flex: 1, padding: "10px 0", borderRadius: 9, border: "none",
                      background: savingBooking ? "rgba(14,34,64,0.10)" : "#1a3a5c",
                      color: "#0E1E38", fontSize: 13, fontWeight: 700, cursor: "pointer",
                    }}
                    onClick={submitNewBooking}
                    disabled={savingBooking}
                  >
                    {savingBooking ? "Creating…" : "Create Booking"}
                  </button>
                  <button
                    style={{
                      padding: "10px 18px", borderRadius: 9,
                      border: "1px solid rgba(100,116,139,0.3)",
                      background: "transparent", color: "rgba(242,235,217,0.4)",
                      fontSize: 13, fontWeight: 700, cursor: "pointer",
                    }}
                    onClick={() => setNewBookingModal(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Click Popup */}
        {popup && (() => {
          const leaderName  = popup.booking.leaderName  || popup.booking.guestname;
          const mainGuest   = popup.booking.guestname;
          const displayPhone = popup.booking.leaderPhone || null;
          const showMainGuest = mainGuest && mainGuest !== "—" && mainGuest !== leaderName;
          return (
            <div style={styles.popup(popup.x, popup.y)} onClick={(e) => e.stopPropagation()}>
              <button style={styles.popupClose} onClick={() => setPopup(null)}>×</button>
              <div style={{ paddingRight: 20, marginBottom: 2 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#F2EBD9" }}>
                  {leaderName}
                </div>
                {displayPhone && (
                  <div style={{ fontSize: 12, color: "rgba(242,235,217,0.4)", marginTop: 1 }}>{displayPhone}</div>
                )}
                {showMainGuest && (
                  <div style={{ fontSize: 11, color: "rgba(242,235,217,0.5)", marginTop: 3 }}>
                    Booked by: {mainGuest}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, marginTop: 6, flexWrap: "wrap" }}>
                <div
                  style={{
                    display: "inline-block",
                    background: `${STATUS_COLOR[popup.booking.status]}22`,
                    border: `1px solid ${STATUS_COLOR[popup.booking.status]}66`,
                    color: STATUS_COLOR[popup.booking.status],
                    borderRadius: 6,
                    padding: "2px 10px",
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  {popup.booking.status}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <PopupRow label="Booking ID" value={popup.booking.bookingid} />
                <PopupRow label="Check-in" value={formatDateShort(popup.booking.checkin)} />
                <PopupRow label="Check-out" value={formatDateShort(popup.booking.checkout)} />
                <PopupRow label="Nights" value={String(popup.booking.nights)} />
                <PopupRow label="Total" value={formatCurrency(popup.booking.total)} highlight />
              </div>
              {conflictBookingIds.has(popup.booking.bookingid) && (
                <div
                  style={{
                    marginTop: 10,
                    background: "rgba(239,68,68,0.1)",
                    border: "1px solid rgba(239,68,68,0.3)",
                    borderRadius: 6,
                    padding: "6px 10px",
                    fontSize: 12,
                    color: "#ef4444",
                    fontWeight: 600,
                  }}
                >
                  <FontAwesomeIcon icon={faTriangleExclamation} /> Booking conflict detected
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </DashboardLayout>
  );
}

const modalInputStyle: React.CSSProperties = {
  background: "#0E1E38",
  border: "1px solid rgba(201,168,76,0.2)",
  borderRadius: 8, padding: "9px 12px",
  color: "#F2EBD9", fontSize: 13, outline: "none",
  fontFamily: "inherit", width: "100%", boxSizing: "border-box",
};

function ModalField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: 1 }}>
      <label style={{ fontSize: 10.5, fontWeight: 700, color: "rgba(242,235,217,0.45)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function TooltipRow({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 3 }}>
      <span style={{ fontSize: 12, color: "rgba(242,235,217,0.45)" }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 600, color: color || "#94a3b8" }}>{value}</span>
    </div>
  );
}

function PopupRow({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
      <span style={{ fontSize: 12, color: "rgba(242,235,217,0.45)" }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 600, color: highlight ? "#C9A84C" : "rgba(242,235,217,0.6)" }}>{value}</span>
    </div>
  );
}
